import { supabase } from "../lib/supabase";
import type { CostCenter, Cycle, Profile, SubmissionVersion, TaskRow, WorkspaceData } from "../types";

interface AssignmentRow {
  cost_center: CostCenter | CostCenter[] | null;
}

interface ChangeRow {
  task_id: string;
  month: number;
  modified_value: number | string;
}
interface DraftJustificationRow {
  task_id: string;
  justification: string;
}


interface TaskQueryRow {
  id: string;
  legacy_key: string;
  task_code: string;
  name: string;
  unit: string;
  baseline_version_number: number;
  operational_definition: string | null;
  criteria: string | null;
  definition_responsible: string | null;
  definition_updated_on: string | null;
  activity: { code: string; name: string; operational_definition: string | null; normative_basis: string | null }
    | Array<{ code: string; name: string; operational_definition: string | null; normative_basis: string | null }>
    | null;
  baseline_targets: Array<{ month: number; value: number | string }>;
  task_source_versions: Array<{
    version_number: number;
    source_document: string;
    source_date: string | null;
    unit: string;
    task_source_version_targets: Array<{ month: number; value: number | string }>;
  }>;
}

interface VersionQueryRow {
  id: string;
  version_number: number;
  justification: string;
  document_reference: string;
  created_by_name: string;
  created_at: string;
  submission_version_task_details: Array<{
    task_id: string;
    justification: string;
  }>;
  submission_version_targets: Array<{
    task_id: string;
    month: number;
    value: number | string;
  }>;
  task_validations: Array<{ gmr_status: string; line_status: string; gmr_comment: string | null; line_comment: string | null }>;
}

function requireClient() {
  if (!supabase) throw new Error("Supabase no está configurado.");
  return supabase;
}

export async function signIn(email: string, password: string) {
  const client = requireClient();
  const { error } = await client.auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export async function signOut() {
  const client = requireClient();
  const { error } = await client.auth.signOut();
  if (error) throw error;
}

export async function loadWorkspace(centerId?: string): Promise<WorkspaceData> {
  const client = requireClient();
  const { data: authData, error: authError } = await client.auth.getUser();
  if (authError || !authData.user) throw authError ?? new Error("Sesión no disponible.");

  const [{ data: profileRow, error: profileError }, { data: assignmentRows, error: assignmentError }] = await Promise.all([
    client.from("profiles").select("id, full_name, email, role").eq("id", authData.user.id).single(),
    client.from("user_cost_centers").select("cost_center:cost_centers(id, code, name)").eq("user_id", authData.user.id),
  ]);
  if (profileError) throw profileError;
  if (assignmentError) throw assignmentError;

  let costCenters = ((assignmentRows ?? []) as unknown as AssignmentRow[]).flatMap((row) => {
    if (!row.cost_center) return [];
    return Array.isArray(row.cost_center) ? row.cost_center : [row.cost_center];
  });
  if (profileRow.role === "admin") {
    const { data, error } = await client.from("cost_centers").select("id, code, name").order("code");
    if (error) throw error;
    costCenters = data ?? [];
  }
  if (!costCenters.length) throw new Error("La cuenta no tiene un centro de costo asignado.");
  const selectedCenterId = centerId && costCenters.some((center) => center.id === centerId)
    ? centerId
    : costCenters[0].id;

  const { data: cycleRow, error: cycleError } = await client
    .from("reprogramming_cycles")
    .select("id, year, name, status, registration_enabled, source_version_id, official_version_number, officialized_at")
    .order("year", { ascending: false })
    .limit(1)
    .single();
  if (cycleError) throw cycleError;

  const [{ data: monthRows, error: monthError }, { data: submissionRow, error: submissionError }, { data: sourceRow, error: sourceError }] = await Promise.all([
    client.from("cycle_months").select("month, enabled").eq("cycle_id", cycleRow.id).order("month"),
    client.from("submissions").select("id, status").eq("cycle_id", cycleRow.id).eq("cost_center_id", selectedCenterId).maybeSingle(),
    client.from("source_versions").select("source_file, imported_at").eq("id", cycleRow.source_version_id).single(),
  ]);
  if (monthError) throw monthError;
  if (submissionError) throw submissionError;
  if (sourceError) throw sourceError;

  const { data: taskRows, error: taskError } = await client
    .from("tasks")
    .select("id, legacy_key, task_code, name, unit, baseline_version_number, operational_definition, criteria, definition_responsible, definition_updated_on, activity:activities(code, name, operational_definition, normative_basis), baseline_targets(month, value), task_source_versions(version_number, source_document, source_date, unit, task_source_version_targets(month, value))")
    .eq("cost_center_id", selectedCenterId)
    .eq("source_version_id", cycleRow.source_version_id)
    .order("task_code");
  if (taskError) throw taskError;

  let changes: ChangeRow[] = [];
  let allowedMonths: number[] | null = null;
  let versions: SubmissionVersion[] = [];
  let taskJustifications: Record<string, string> = {};
  let latestValidationRows: VersionQueryRow["task_validations"] = [];
  if (submissionRow?.id) {
    const [{ data: changeRows, error: changeError }, { data: permissionRows, error: permissionError }, { data: versionRows, error: versionError }, { data: justificationRows, error: justificationError }] = await Promise.all([
      client.from("target_changes").select("task_id, month, modified_value").eq("submission_id", submissionRow.id),
      client.from("submission_month_permissions").select("month").eq("submission_id", submissionRow.id).eq("enabled", true).order("month"),
      client.from("submission_versions").select("id, version_number, justification, document_reference, created_by_name, created_at, submission_version_targets(task_id, month, value), submission_version_task_details(task_id, justification), task_validations(gmr_status, line_status, gmr_comment, line_comment)").eq("submission_id", submissionRow.id).order("version_number"),
      client.from("draft_task_justifications").select("task_id, justification").eq("submission_id", submissionRow.id),
    ]);
    if (changeError) throw changeError;
    if (permissionError) throw permissionError;
    if (versionError) throw versionError;
    changes = changeRows ?? [];
    if (justificationError) throw justificationError;
    if (permissionRows?.length) allowedMonths = permissionRows.map((row) => row.month);
    taskJustifications = Object.fromEntries(((justificationRows ?? []) as DraftJustificationRow[]).map((item) => [item.task_id, item.justification]));
    versions = ((versionRows ?? []) as unknown as VersionQueryRow[]).map((version) => {
      const byTask = new Map<string, number[]>();
      for (const target of version.submission_version_targets ?? []) {
        const values = byTask.get(target.task_id) ?? Array(12).fill(0);
        values[target.month - 1] = Number(target.value);
        byTask.set(target.task_id, values);
      }
      const justificationByTask = new Map((version.submission_version_task_details ?? []).map((item) => [item.task_id, item.justification]));
      return { id: version.id, versionNumber: version.version_number, justification: version.justification, documentReference: version.document_reference, createdByName: version.created_by_name, createdAt: version.created_at, targets: [...byTask].map(([taskId, values]) => ({ taskId, values, justification: justificationByTask.get(taskId) ?? version.justification })) };
    });
    latestValidationRows = ((versionRows ?? []) as unknown as VersionQueryRow[]).at(-1)?.task_validations ?? [];
  }

  const tasks: TaskRow[] = ((taskRows ?? []) as unknown as TaskQueryRow[]).map((row) => {
    const baseline = Array(12).fill(0);
    for (const item of row.baseline_targets ?? []) baseline[item.month - 1] = Number(item.value);
    const modified = Array<number | null>(12).fill(null);
    for (const item of changes.filter((change) => change.task_id === row.id)) {
      modified[item.month - 1] = Number(item.modified_value);
    }
    const activity = Array.isArray(row.activity) ? row.activity[0] : row.activity;
    const sourceVersions = (row.task_source_versions ?? []).map((version) => {
      const values = Array(12).fill(0);
      for (const target of version.task_source_version_targets ?? []) values[target.month - 1] = Number(target.value);
      return { versionNumber: version.version_number, sourceDocument: version.source_document, sourceDate: version.source_date, unit: version.unit, values };
    }).sort((a, b) => a.versionNumber - b.versionNumber);
    return {
      id: row.id,
      legacyKey: row.legacy_key,
      activityCode: activity?.code ?? "",
      activityName: activity?.name ?? "",
      taskCode: row.task_code,
      taskName: row.name,
      baselineVersionNumber: row.baseline_version_number ?? 0,
      unit: row.unit,
      operationalDefinition: row.operational_definition,
      criteria: row.criteria,
      activityOperationalDefinition: activity?.operational_definition ?? null,
      normativeBasis: activity?.normative_basis ?? null,
      definitionResponsible: row.definition_responsible,
      definitionUpdatedOn: row.definition_updated_on,
      baseline,
      sourceVersions,
      modified,
    };
  });

  const profile: Profile = {
    id: profileRow.id,
    fullName: profileRow.full_name,
    email: profileRow.email,
    role: profileRow.role,
  };
  const cycle: Cycle = {
    id: cycleRow.id,
    year: cycleRow.year,
    name: cycleRow.name,
    status: cycleRow.status,
    registrationEnabled: cycleRow.registration_enabled,
    officialVersionNumber: cycleRow.official_version_number,
    officializedAt: cycleRow.officialized_at,
    months: monthRows ?? [],
    sourceFile: sourceRow.source_file,
    sourceImportedAt: sourceRow.imported_at,
  };
  const selectedCenter = costCenters.find((center) => center.id === selectedCenterId)!;
  const isUdr = /^03\.[0-9]{2}\.[0-9]{2}$/.test(selectedCenter.code);
  const validationStatus = !isUdr ? "not_required"
    : !versions.length ? "not_submitted"
      : latestValidationRows.some((item) => item.gmr_status === "observed") ? "observed_gmr"
        : latestValidationRows.some((item) => item.line_status === "observed") ? "observed_line"
          : submissionRow?.status === "draft" ? "not_submitted"
          : latestValidationRows.some((item) => item.gmr_status === "pending") ? "pending_gmr"
            : latestValidationRows.some((item) => item.line_status === "pending" || item.line_status === "waiting") ? "pending_line"
              : "accepted";
  const observedValidation = latestValidationRows.find((item) => item.gmr_status === "observed" || item.line_status === "observed");
  const validationObservation = observedValidation?.gmr_status === "observed"
    ? observedValidation.gmr_comment
    : observedValidation?.line_comment ?? null;
  return {
    profile,
    costCenters,
    selectedCenterId,
    cycle,
    submissionId: submissionRow?.id ?? null,
    submissionStatus: submissionRow?.status ?? "draft",
    tasks,
    allowedMonths,
    versions,
    validationStatus,
    validationObservation,
    taskJustifications,
  };
}

export async function saveTargetValue(cycleId: string, centerId: string, taskId: string, month: number, value: number) {
  const client = requireClient();
  const { data, error } = await client.rpc("set_target_value", {
    p_cycle_id: cycleId,
    p_cost_center_id: centerId,
    p_task_id: taskId,
    p_month: month,
    p_value: value,
  });
  if (error) throw error;
  return data as string;
}

export async function closeReprogramming(cycleId: string, centerId: string, taskJustifications: Record<string, string>, documentReference: string) {
  const client = requireClient();
  const { data, error } = await client.rpc("close_center_reprogramming", {
    p_cycle_id: cycleId,
    p_cost_center_id: centerId,
    p_task_justifications: taskJustifications,
    p_document_reference: documentReference,
  });
  if (error) throw error;
  return data as string;
}

export async function saveTaskJustification(cycleId: string, centerId: string, taskId: string, justification: string) {

  const client = requireClient();
  const { error } = await client.rpc("set_task_justification", {
    p_cycle_id: cycleId,
    p_cost_center_id: centerId,
    p_task_id: taskId,
    p_justification: justification,
  });
  if (error) throw error;
}


export async function setRegistrationEnabled(cycleId: string, enabled: boolean) {
  const client = requireClient();
  const { error } = await client.from("reprogramming_cycles").update({ registration_enabled: enabled }).eq("id", cycleId);
  if (error) throw error;
}

export async function setMonthEnabled(cycleId: string, month: number, enabled: boolean) {
  const client = requireClient();
  const { error } = await client.from("cycle_months").update({ enabled }).eq("cycle_id", cycleId).eq("month", month);
  if (error) throw error;
}
